import { providers } from '../../providers';
import { baidu } from '../baidu';
import { cool } from '../cool';
import { fal } from '../fal';
import { grsai } from '../grsai';
import { minimax } from '../minimax';
import { suchuang } from '../suchuang';
import { wavespeed } from '../wavespeed';
import { getUiOptionsForModel } from '../ui-options';
import type { VideoModelEntry } from '../types';

// 官方 MiniMax 价格表: 人民币/秒
// https://platform.minimaxi.com/docs/guides/pricing-paygo
const H3 = { '2k': 0.8, '768p': 0.5 };
const H02_512 = { 6: 0.60, 10: 1.00 };
const H02_768 = { 6: 2.00, 10: 4.00 };
const H02_1080 = { 6: 3.5 };
const H23F_768 = { 6: 1.35, 10: 2.31 };
const H23F_1080 = { 6: 2.25 };
const H23_768 = { 6: 2, 10: 4 };
const H23_1080 = { 6: 3.5 };
const H3_OW = { '480p': 0.04, '768p': 0.08 };
const H3_OW_REF = { '480p': 0.05, '768p': 0.10 };
const H3_OW_REF_AUDIO = 0.02;
const H3_OW_REF_VIDEO_PER_SEC = 0.05;
const H3_MAX_FAL = { '480p': 0.05, '768p': 0.08 };
// 查找价格函数
const lookup = (table: Record<number, number>, duration: number) =>
    table[duration] ?? Math.max(...Object.values(table));
const lookupQuality = (table: Record<string, number>, quality?: string) =>
    table[quality?.toLowerCase() ?? ''] ?? Math.max(...Object.values(table));
// 美元汇率
const RMB_TO_USD = 1 / 6.8;
// rmb to usd 转换
const usd = (rmb: number) => rmb * RMB_TO_USD;
// 系数
const COST_FACTOR = 1.5;

const minimaxH3OpenCost = {
    getCost: ({ duration, quality, providerOptions }: { duration?: number; quality?: string; providerOptions?: Record<string, any> }) => {
        const billed = Math.min(15, Math.max(5, duration || 5));
        const isRef = providerOptions?._frameMode === 'ref';
        const rate = lookupQuality(isRef ? H3_OW_REF : H3_OW, quality);
        let cost = billed * rate;
        if (isRef) {
            const inputVideoSec = providerOptions?._media?.videoDurationSeconds ?? 0;
            const audioCount = providerOptions?._media?.audioCount ?? 0;
            cost += inputVideoSec * H3_OW_REF_VIDEO_PER_SEC;
            cost += audioCount * H3_OW_REF_AUDIO;
        }
        return cost * COST_FACTOR;
    },
    getPassCount: ({ duration, quality, providerOptions }: { duration?: number; quality?: string; providerOptions?: Record<string, any> }) => {
        const billed = Math.min(15, Math.max(5, duration || 5));
        const isRef = providerOptions?._frameMode === 'ref';
        const rate = lookupQuality(isRef ? H3_OW_REF : H3_OW, quality);
        let cost = billed * rate;
        if (isRef) {
            const inputVideoSec = providerOptions?._media?.videoDurationSeconds ?? 0;
            const audioCount = providerOptions?._media?.audioCount ?? 0;
            cost += inputVideoSec * H3_OW_REF_VIDEO_PER_SEC;
            cost += audioCount * H3_OW_REF_AUDIO;
        }
        return cost * COST_FACTOR * 100;
    },
};

export const hailuoModels: VideoModelEntry[] = [
    {
        id: 'hailuo-h3',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: grsai('hailuo-h3'),
                modelBackup: [cool('hailuo-h3'), baidu('hailuo-h3')],
                getCost: ({ duration, quality, providerOptions }) => {
                    const inputVideoSec = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const rate = lookupQuality(H3, quality);
                    return usd((duration + inputVideoSec) * rate) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality, providerOptions }) => {
                    const inputVideoSec = providerOptions?._media?.videoDurationSeconds ?? 0;
                    const rate = lookupQuality(H3, quality);
                    return usd((duration + inputVideoSec) * rate) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['768P', '2K'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['768P', '2K'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: [],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['768P', '2K'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9'],
                durations: [4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['768P', '2K'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15s', formats: ['video/mp4', 'video/quicktime'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15s', formats: ['audio/mpeg', 'audio/wav'] },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'minimax-h3-open',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: wavespeed('minimax-h3-open'),
                getCost: minimaxH3OpenCost.getCost,
                getPassCount: minimaxH3OpenCost.getPassCount,
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '768p'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '768p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: [],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '768p'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['16:9', '9:16', '1:1', '4:3', '3:4', '21:9', '9:21'],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480p', '768p'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15s', formats: ['video/mp4', 'video/quicktime'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15s', formats: ['audio/mpeg', 'audio/wav'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15s' },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('minimax-h3-open'),
    },
    {
        id: 'minimax-h3-max',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: fal('minimax-h3-max'),
                getCost: ({ duration, quality }) => {
                    const billed = Math.min(15, Math.max(5, duration || 5));
                    return billed * lookupQuality(H3_MAX_FAL, quality) * COST_FACTOR;
                },
                getPassCount: ({ duration, quality }) => {
                    const billed = Math.min(15, Math.max(5, duration || 5));
                    return billed * lookupQuality(H3_MAX_FAL, quality) * COST_FACTOR * 100;
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: ['21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480P', '768P'],
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480P', '768P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: [],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480P', '768P'],
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            ref: {
                aspectRatios: ['match', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'],
                durations: [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15],
                qualities: ['480P', '768P'],
                inlineMedia: {
                    allowInline: true,
                    image: { allow: true, maxCount: 9, maxSize: '30MB', formats: ['image/png', 'image/jpeg', 'image/webp'] },
                    video: { allow: true, maxCount: 3, maxSize: '50MB', minDuration: '2s', maxDuration: '15s', formats: ['video/mp4', 'video/quicktime'] },
                    audio: { allow: true, maxCount: 3, maxSize: '15MB', minDuration: '2s', maxDuration: '15s', formats: ['audio/mpeg', 'audio/wav'] },
                    mediaDurationLimit: { enabled: true, maxTotalDuration: '15s' },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
            ref: ['prompt', 'image|video'],
        },
        supportsEdit: true,
        supportsInput: ['t2v', 'first', 'first_last', 'reference'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
        uiOptions: getUiOptionsForModel('minimax-h3-max'),
        pollStrategy: {
            phases: [
                { every: '1s', count: 30 },
                { every: '5s', count: 60 },
                { every: '30s' },
            ],
            maxAttempts: 120,
        },
    },
    {
        id: 'hailuo-02',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: baidu('hailuo-02'),
                getCost: ({ duration, quality }) => {
                    if (quality === '512p') {
                        return usd(lookup(H02_512, duration)) * COST_FACTOR;
                    } else if (quality === '768p') {
                        return usd(lookup(H02_768, duration)) * COST_FACTOR;
                    } else if (quality === '1080p') {
                        return usd(lookup(H02_1080, duration)) * COST_FACTOR;
                    } else {
                        return usd(lookup(H02_1080, duration)) * COST_FACTOR;
                    }
                },
                getPassCount: ({ duration, quality }) => {
                    if (quality === '512p') {
                        return usd(lookup(H02_512, duration)) * COST_FACTOR * 100;
                    } else if (quality === '768p') {
                        return usd(lookup(H02_768, duration)) * COST_FACTOR * 100;
                    } else if (quality === '1080p') {
                        return usd(lookup(H02_1080, duration)) * COST_FACTOR * 100;
                    } else {
                        return usd(lookup(H02_1080, duration)) * COST_FACTOR * 100;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['768p', '1080p'],
                qualityByDuration: { 10: ['768p'] },
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['512p', '768p', '1080p'],
                qualityByDuration: { 10: ['512p', '768p'] },
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first_last: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['768p', '1080p'],
                qualityByDuration: { 10: ['768p'] },
                inlineMedia: {
                    image: { allow: true, maxCount: 2, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
            first_last: ['prompt', 'image2'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first', 'first_last'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'hailuo-2.3-fast',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: baidu('hailuo-2.3-fast'),
                getCost: ({ duration, quality }) => {
                    if (quality === '768p') {
                        return usd(lookup(H23F_768, duration)) * COST_FACTOR;
                    } else if (quality === '1080p') {
                        return usd(lookup(H23F_1080, duration)) * COST_FACTOR;
                    } else {
                        return usd(lookup(H23F_1080, duration)) * COST_FACTOR;
                    }
                },
                getPassCount: ({ duration, quality }) => {
                    if (quality === '768p') {
                        return usd(lookup(H23F_768, duration)) * COST_FACTOR * 100;
                    } else if (quality === '1080p') {
                        return usd(lookup(H23F_1080, duration)) * COST_FACTOR * 100;
                    } else {
                        return usd(lookup(H23F_1080, duration)) * COST_FACTOR * 100;
                    }
                },
            },
        ],
        configByMode: {
            first: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['768p', '1080p'],
                qualityByDuration: { 10: ['768p'] },
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            first: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['first'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
    {
        id: 'hailuo-2.3',
        chef: providers['minimax'],
        providers: [
            {
                ...providers.minimax,
                model: baidu('hailuo-2.3'),
                getCost: ({ duration, quality }) => {
                    if (quality === '768p') {
                        return usd(lookup(H23_768, duration)) * COST_FACTOR;
                    } else if (quality === '1080p') {
                        return usd(lookup(H23_1080, duration)) * COST_FACTOR;
                    } else {
                        return usd(lookup(H23_1080, duration)) * COST_FACTOR;
                    }
                },
                getPassCount: ({ duration, quality }) => {
                    if (quality === '768p') {
                        return usd(lookup(H23_768, duration)) * COST_FACTOR * 100;
                    } else if (quality === '1080p') {
                        return usd(lookup(H23_1080, duration)) * COST_FACTOR * 100;
                    } else {
                        return usd(lookup(H23_1080, duration)) * COST_FACTOR * 100;
                    }
                },
            },
        ],
        configByMode: {
            none: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['768p', '1080p'],
                qualityByDuration: { 10: ['768p'] },
                inlineMedia: {
                    image: { allow: false },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
            first: {
                aspectRatios: [],
                durations: [6, 10],
                qualities: ['768p', '1080p'],
                qualityByDuration: { 10: ['768p'] },
                inlineMedia: {
                    image: { allow: true, maxCount: 1, maxSize: '10MB', formats: ['image/png', 'image/jpeg'] },
                    video: { allow: false },
                    audio: { allow: false },
                },
            },
        },
        precheckByMode: {
            none: ['prompt'],
            first: ['prompt', 'image'],
        },
        supportsEdit: false,
        supportsInput: ['t2v', 'first'],
        priceIndicator: 'lowest',
        price: 'Dynamic',
    },
];